import {
  deriveBlockMultiSelectionFromSelection,
} from "../selection/block-multi-selection";
import {
  deriveRowMultiSelectionFromSelection,
} from "../selection/row-multi-selection";
import type { Block } from "../types";
import * as mutations from "../mutations/index";
import {
  applyDocumentCommit,
  cloneEditorSlice,
  ingestDocument,
} from "../document-lifecycle";
import {
  cloneDocument,
  HISTORY_LIMIT,
  redoHistory,
  undoHistory,
} from "../history";
import { createEmptyDocument } from "../factories";
import { resolveTextEditingBlockAfterHistoryNavigation } from "../text-editing";
import {
  EDITOR_ADD_COLUMN,
  EDITOR_COPY_SELECTED_BLOCKS,
  EDITOR_CUT_SELECTED_BLOCKS,
  EDITOR_DELETE_GROUP,
  EDITOR_DELETE_SELECTED,
  EDITOR_DUPLICATE_SELECTED,
  EDITOR_FLUSH_HISTORY,
  EDITOR_INSERT_BLOCK,
  EDITOR_INSERT_GROUP,
  EDITOR_INSERT_ROW,
  EDITOR_INSERT_ROW_WITH_BLOCK,
  EDITOR_INSERT_SNIPPET,
  EDITOR_LOAD_DOCUMENT,
  EDITOR_MOVE_BLOCK,
  EDITOR_MOVE_BLOCK_TO_NEW_ROW,
  EDITOR_MOVE_ROW,
  EDITOR_MOVE_ROW_BETWEEN_PARENTS,
  EDITOR_MOVE_ROW_IN_GROUP,
  EDITOR_MOVE_SELECTED,
  EDITOR_MOVE_TOP_LEVEL_SECTION,
  EDITOR_MOVE_CONTIGUOUS_TOP_LEVEL_ROWS,
  EDITOR_MOVE_CONTIGUOUS_GROUP_ROWS,
  EDITOR_PASTE_BLOCKS_FROM_CLIPBOARD,
  EDITOR_REDO,
  EDITOR_REMOVE_COLUMN,
  EDITOR_SET_COLUMN_WIDTHS,
  EDITOR_SET_BLOCK_SELECTION,
  EDITOR_SET_ROW_SELECTION,
  EDITOR_SET_MODE,
  EDITOR_SET_SAVE_STATUS,
  EDITOR_SET_SELECTION,
  EDITOR_SET_TEXT_EDITING_BLOCK,
  EDITOR_SET_TEXT_EDIT_REENTRY_GUARD,
  EDITOR_UNDO,
  EDITOR_UNGROUP,
  EDITOR_GROUP_ROWS,
  EDITOR_UPDATE_BLOCK,
  EDITOR_UPDATE_COLUMN,
  EDITOR_UPDATE_GROUP,
  EDITOR_UPDATE_ROW,
  EDITOR_UPDATE_SETTINGS,
  type EditorAction,
  type EditorSliceState,
} from "./types";

function syncDerivedSelectionState(state: EditorSliceState): void {
  const blockDerived = deriveBlockMultiSelectionFromSelection(state.selection);
  state.selectedBlockIds = blockDerived.selectedBlockIds;
  state.primaryBlockId = blockDerived.primaryBlockId;
  state.anchorBlockId = blockDerived.anchorBlockId;

  const rowDerived = deriveRowMultiSelectionFromSelection(state.selection);
  state.selectedRowIds = rowDerived.selectedRowIds;
  state.primaryRowId = rowDerived.primaryRowId;
  state.anchorRowId = rowDerived.anchorRowId;
}

export function createInitialEditorState(): EditorSliceState {
  return {
    document: ingestDocument(createEmptyDocument()),
    templateId: 'blank',
    selection: { kind: 'email' },
    selectedBlockIds: [],
    primaryBlockId: null,
    anchorBlockId: null,
    selectedRowIds: [],
    primaryRowId: null,
    anchorRowId: null,
    blockClipboard: [],
    textEditingBlock: null,
    textEditReentryGuard: null,
    mode: 'design',
    saveStatus: 'idle',
    past: [],
    future: [],
  };
}

export function editorReducer(
  state: EditorSliceState = createInitialEditorState(),
  action: EditorAction,
): EditorSliceState {
  switch (action.type) {
    case EDITOR_SET_MODE: {
      const next = cloneEditorSlice(state);
      next.mode = action.payload;
      return next;
    }
    case EDITOR_SET_SELECTION: {
      const next = cloneEditorSlice(state);
      mutations.applySelectionChange(next, action.payload);
      return next;
    }
    case EDITOR_SET_BLOCK_SELECTION: {
      const next = cloneEditorSlice(state);
      mutations.applyBlockMultiSelectionChange(next, action.payload);
      return next;
    }
    case EDITOR_SET_ROW_SELECTION: {
      const next = cloneEditorSlice(state);
      mutations.applyRowMultiSelectionChange(next, action.payload);
      return next;
    }
    case EDITOR_SET_TEXT_EDITING_BLOCK: {
      const next = cloneEditorSlice(state);
      next.textEditingBlock = action.payload;
      if (action.payload) {
        next.textEditReentryGuard = null;
      }
      return next;
    }
    case EDITOR_SET_TEXT_EDIT_REENTRY_GUARD: {
      const next = cloneEditorSlice(state);
      next.textEditReentryGuard = action.payload;
      return next;
    }
    case EDITOR_SET_SAVE_STATUS: {
      return { ...state, saveStatus: action.payload };
    }
    case EDITOR_LOAD_DOCUMENT: {
      const next = cloneEditorSlice(state);
      next.document = ingestDocument(cloneDocument(action.payload.document));
      next.templateId = action.payload.templateId;
      next.selection = { kind: 'email' };
      next.selectedBlockIds = [];
      next.primaryBlockId = null;
      next.anchorBlockId = null;
      next.selectedRowIds = [];
      next.primaryRowId = null;
      next.anchorRowId = null;
      next.blockClipboard = [];
      next.textEditingBlock = null;
      next.textEditReentryGuard = null;
      next.past = [];
      next.future = [];
      next.saveStatus = 'idle';
      return next;
    }
    case EDITOR_UNDO: {
      const next = cloneEditorSlice(state);
      const textEditingBefore = state.textEditingBlock;
      if (!undoHistory(next)) {
        return state;
      }
      const preservedTextEditing = resolveTextEditingBlockAfterHistoryNavigation(
        next.document,
        textEditingBefore,
      );
      next.textEditingBlock = preservedTextEditing;
      if (preservedTextEditing !== null) {
        next.selection = {
          kind: "block",
          rowId: preservedTextEditing.rowId,
          columnId: preservedTextEditing.columnId,
          blockId: preservedTextEditing.blockId,
        };
      }
      next.textEditReentryGuard = null;
      syncDerivedSelectionState(next);
      return next;
    }
    case EDITOR_REDO: {
      const next = cloneEditorSlice(state);
      const textEditingBefore = state.textEditingBlock;
      if (!redoHistory(next)) {
        return state;
      }
      const preservedTextEditing = resolveTextEditingBlockAfterHistoryNavigation(
        next.document,
        textEditingBefore,
      );
      next.textEditingBlock = preservedTextEditing;
      if (preservedTextEditing !== null) {
        next.selection = {
          kind: "block",
          rowId: preservedTextEditing.rowId,
          columnId: preservedTextEditing.columnId,
          blockId: preservedTextEditing.blockId,
        };
      }
      next.textEditReentryGuard = null;
      syncDerivedSelectionState(next);
      return next;
    }
    case EDITOR_FLUSH_HISTORY: {
      const next = cloneEditorSlice(state);
      next.past = [...next.past, action.payload];
      if (next.past.length > HISTORY_LIMIT) {
        next.past.shift();
      }
      next.future = [];
      return next;
    }
    case EDITOR_UPDATE_SETTINGS:
      return applyDocumentCommit(state, draft => {
        mutations.updateSettings(draft, action.payload);
      });
    case EDITOR_UPDATE_ROW:
      return applyDocumentCommit(state, draft => {
        mutations.updateRow(draft, action.payload.rowId, action.payload.patch);
      });
    case EDITOR_UPDATE_GROUP:
      return applyDocumentCommit(state, draft => {
        mutations.updateGroup(
          draft,
          action.payload.groupId,
          action.payload.patch,
        );
      });
    case EDITOR_UPDATE_COLUMN:
      return applyDocumentCommit(state, draft => {
        mutations.updateColumn(
          draft,
          action.payload.rowId,
          action.payload.columnId,
          action.payload.patch,
        );
      });
    case EDITOR_UPDATE_BLOCK:
      return applyDocumentCommit(
        state,
        draft => {
          mutations.updateBlock(
            draft,
            action.payload.rowId,
            action.payload.columnId,
            action.payload.blockId,
            action.payload.patch,
          );
        },
        { history: action.payload.history },
      );
    case EDITOR_INSERT_BLOCK:
      return applyDocumentCommit(state, draft => {
        mutations.insertBlock(
          draft,
          action.payload.rowId,
          action.payload.columnId,
          action.payload.index,
          action.payload.blockType,
        );
      });
    case EDITOR_MOVE_BLOCK:
      return applyDocumentCommit(state, draft => {
        mutations.moveBlock(draft, action.payload.from, action.payload.to);
      });
    case EDITOR_INSERT_ROW_WITH_BLOCK:
      return applyDocumentCommit(state, draft => {
        mutations.insertRowWithBlock(
          draft,
          action.payload.target,
          action.payload.blockType,
        );
      });
    case EDITOR_MOVE_BLOCK_TO_NEW_ROW:
      return applyDocumentCommit(state, draft => {
        mutations.moveBlockToNewRow(
          draft,
          action.payload.from,
          action.payload.target,
        );
      });
    case EDITOR_INSERT_ROW:
      return applyDocumentCommit(state, draft => {
        mutations.insertRow(
          draft,
          action.payload.target,
          action.payload.presetId,
        );
      });
    case EDITOR_INSERT_GROUP:
      return applyDocumentCommit(state, draft => {
        mutations.insertGroup(
          draft,
          action.payload.index,
          action.payload.group,
        );
      });
    case EDITOR_INSERT_SNIPPET:
      return applyDocumentCommit(state, draft => {
        mutations.insertSnippet(
          draft,
          action.payload.snippetId,
          action.payload.index,
        );
      });
    case EDITOR_MOVE_ROW:
      return applyDocumentCommit(state, draft => {
        mutations.moveRow(
          draft,
          action.payload.fromIndex,
          action.payload.toIndex,
        );
      });
    case EDITOR_MOVE_TOP_LEVEL_SECTION:
      return applyDocumentCommit(state, draft => {
        mutations.moveTopLevelSection(
          draft,
          action.payload.fromIndex,
          action.payload.toIndex,
        );
      });
    case EDITOR_MOVE_CONTIGUOUS_TOP_LEVEL_ROWS:
      return applyDocumentCommit(state, draft => {
        mutations.moveContiguousTopLevelRowsToSlot(
          draft,
          action.payload.rowIds,
          action.payload.slotIndex,
        );
      });
    case EDITOR_MOVE_CONTIGUOUS_GROUP_ROWS:
      return applyDocumentCommit(state, draft => {
        mutations.moveContiguousGroupRowsToSlot(
          draft,
          action.payload.groupId,
          action.payload.rowIds,
          action.payload.slotIndex,
        );
      });
    case EDITOR_MOVE_ROW_IN_GROUP:
      return applyDocumentCommit(state, draft => {
        mutations.moveRowInGroup(
          draft,
          action.payload.groupId,
          action.payload.fromIndex,
          action.payload.toIndex,
        );
      });
    case EDITOR_MOVE_ROW_BETWEEN_PARENTS:
      return applyDocumentCommit(state, draft => {
        mutations.moveRowBetweenParents(
          draft,
          action.payload.rowId,
          action.payload.to,
        );
      });
    case EDITOR_UNGROUP:
      return applyDocumentCommit(state, draft => {
        mutations.ungroup(draft, action.payload);
      });
    case EDITOR_GROUP_ROWS:
      return applyDocumentCommit(state, draft => {
        mutations.groupRows(draft, action.payload);
      });
    case EDITOR_DELETE_GROUP:
      return applyDocumentCommit(state, draft => {
        mutations.deleteGroup(
          draft,
          action.payload.groupId,
          action.payload.mode,
        );
      });
    case EDITOR_SET_COLUMN_WIDTHS:
      return applyDocumentCommit(
        state,
        draft => {
          mutations.setColumnWidths(
            draft,
            action.payload.rowId,
            action.payload.widths,
          );
        },
        { history: action.payload.history },
      );
    case EDITOR_ADD_COLUMN:
      return applyDocumentCommit(state, draft => {
        mutations.addColumn(
          draft,
          action.payload.rowId,
          action.payload.afterColumnId,
        );
      });
    case EDITOR_REMOVE_COLUMN:
      return applyDocumentCommit(state, draft => {
        mutations.removeColumn(
          draft,
          action.payload.rowId,
          action.payload.columnId,
        );
      });
    case EDITOR_DUPLICATE_SELECTED:
      return applyDocumentCommit(state, draft => {
        mutations.duplicateSelected(draft);
      });
    case EDITOR_DELETE_SELECTED:
      return applyDocumentCommit(state, draft => {
        mutations.deleteSelected(draft);
      });
    case EDITOR_MOVE_SELECTED:
      return applyDocumentCommit(state, draft => {
        mutations.moveSelected(draft, action.payload);
      });
    case EDITOR_COPY_SELECTED_BLOCKS: {
      const next = cloneEditorSlice(state);
      next.blockClipboard = mutations.copySelectedBlocks(next);
      return next;
    }
    case EDITOR_CUT_SELECTED_BLOCKS: {
      let clipboard: Block[] = [];
      const next = applyDocumentCommit(state, draft => {
        clipboard = mutations.cutSelectedBlocksToClipboard(draft);
        return clipboard.length > 0;
      });
      next.blockClipboard = clipboard;
      return next;
    }
    case EDITOR_PASTE_BLOCKS_FROM_CLIPBOARD:
      return applyDocumentCommit(state, draft =>
        mutations.pasteBlocksFromEditorClipboard(draft, state.blockClipboard),
      );
    default:
      return state;
  }
}
